<script setup lang="ts">
import { computed } from 'vue'
import { useLiveOpsChallengeLeaderboard } from '@/composables/game/leader-board'
import { useLiveOpsChallenge, mapPrizePoolToChallengeFormat } from '@/composables/game/live-ops-challenge'
import { useLiveOpsChallengeModal } from '@/composables/game/live-ops-challenge-modal'
import { useNow } from '@/composables/game/time'
import { formatDuration } from '@/helpers/date'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import BaseChallengeOverlay from '@/components/overlays/BaseChallengeOverlay.vue'
import JoystickIcon from '@/assets/images/challenges/joystick-icon.webp'
import CheckCircleIcon from '@/assets/images/check-circle-transparent.png'
import { LiveOpsLeaderboardType } from '@/types/live-ops'

defineProps<{
	show: boolean
}>()
const emit = defineEmits(['hide'])

const { now } = useNow()
const {
  getLiveOpsChallengeLeaderboard,
  liveOpsChallengeLeaderboard: players,
  currentPlayer,
} = useLiveOpsChallengeLeaderboard()
const { leaderboards } = useLiveOpsChallenge()
const { selectedLeaderboardId } = useLiveOpsChallengeModal()

const selectedLeaderboard = computed(() =>
  leaderboards.value.find(lb => lb.id === selectedLeaderboardId.value) ?? null,
)

const challengeData = computed(() => {
  if (!selectedLeaderboard.value) return null
  return mapPrizePoolToChallengeFormat(selectedLeaderboard.value)
})

const leaderboardName = computed(() => selectedLeaderboard.value?.name ?? '')
const leaderboardId = computed(() => selectedLeaderboard.value?.id ?? '')

const mainColor = computed(() => selectedLeaderboard.value?.mainColor ?? '#6366F1')

const leaderboardDescription = computed(() =>
  selectedLeaderboard.value?.description
    ?? 'Compete with other players, climb the ranks, and unlock your prize!',
)

const mainImage = computed(() => selectedLeaderboard.value?.mainIconUrl || JoystickIcon)

const ruleLines = computed(() => {
  const rules = selectedLeaderboard.value?.rules
})

const backgroundStyle = computed(() => ({
  background: `linear-gradient(180deg, ${mainColor.value} 0%, ${mainColor.value} 24.02%, rgba(30, 27, 75, 0.00) 100%)`,
}))

const timerBackgroundInlineStyle = computed(() => ({
  background: `linear-gradient(90deg, ${mainColor.value} 0%, ${mainColor.value}99 50%, #1E1B4B 100%)`,
}))

const isCollectibleChallenge = computed(() => {
  const config = selectedLeaderboard.value?.config
  return !!config && 'currency' in config
})

const challengeCurrencyId = computed(() => {
  const config = selectedLeaderboard.value?.config
  if (config && 'currency' in config) return config.currency
  return undefined
})

const currencyConfig = computed(() => {
  const id = challengeCurrencyId.value
  if (!id) return undefined
  return allSpecialCurrenciesConfig.value[id]
})

const scoreImage = computed(() => {
  if (isCollectibleChallenge.value) return currencyConfig.value?.image ?? CheckCircleIcon
  return CheckCircleIcon
})

const countdown = computed(() => {
  if (!selectedLeaderboard.value) return 0
  return Math.max(0, selectedLeaderboard.value.endTimestamp - now.value)
})

const formattedCountdown = computed(() => formatDuration(
  countdown.value,
  undefined,
  countdown.value > 3600 * 72,
))

function getLeaderboard() {
  return getLiveOpsChallengeLeaderboard(leaderboardId.value)
}

const ruleLine1 = computed(() => isCollectibleChallenge.value ? 'Collect' : selectedLeaderboard.value?.rules)
const ruleLine2 = computed(() => {
  if (isCollectibleChallenge.value && selectedLeaderboard.value?.config.type === LiveOpsLeaderboardType.REWARDED_CURRENCY) {
    return currencyConfig.value?.label
  }
  return undefined
})

const ruleCardImage = computed(() => {
  if (isCollectibleChallenge.value && selectedLeaderboard.value?.config.type === LiveOpsLeaderboardType.REWARDED_CURRENCY) {
    return currencyConfig.value?.image
  }
  return undefined
})
</script>

<template>
  <BaseChallengeOverlay
    :show="show"
    :challenge="challengeData"
    :formatted-countdown="formattedCountdown"
    hide-cta-button
    :get-leaderboard="getLeaderboard"
    :players="players"
    :current-player="currentPlayer"
    :background-style="backgroundStyle"
    :main-image="mainImage"
    main-image-class="h-auto"
    :timer-background-inline-style="timerBackgroundInlineStyle"
    :title="leaderboardName"
    :description="leaderboardDescription"
    :rule-card-line1="ruleLine1"
    :rule-card-line2="ruleLine2"
    :rule-card-image="ruleCardImage"
    :score-image="scoreImage"
    :challenge-currency="challengeCurrencyId"
    @hide="emit('hide')"
  />
</template>
